import PptxGenJS from 'pptxgenjs';
import { isSequentialMonadicMultiSelectQuestion, QUESTION_TYPES } from '@api/instant_survey';
import Highcharts from 'highcharts';
import 'highcharts/modules/wordcloud';
import { svgToPngDataUrl, THEMES_DARK, WORD_CLOUD_COLORS } from '@utilities';
import { buildWordCloudChartOptions } from '@utilities/charts/WordCloud/utilities/helpers';
import {
  createContSumChartSlide,
  createGridChartSlide,
  createGridTableSlide,
  createMaxDiffChartSlide,
  createMaxDiffTableSlide,
  createMetadataSlide,
  createNumericTableSlide,
  createRankChartSlide,
  createRankTableSlide,
  createSurveyChartSlide,
  createTableSlide,
  createTextSlide,
  createTitleSlide,
  createVanWestendorpChartSlide,
  createWordCloudSlide,
} from '@utilities/powerpoint';
import {
  getNumericKeyFindingsForPowerPoint,
  getTextSummariesForPowerPoint,
} from './keyFindingsFilter';
import { normalizeAnswerOptionChart } from '../components/Responses/components/ComparisonCharts/utilities/helpers';
import { getDefaultAnswerOptionSelection } from '../components/Responses/utilities/helpers';
import { SEARCH_PARAM_KEYS } from '../../../utilities/constants';
import { isLanguageScreenerQuestion } from '../../../../CreateInstantSurvey/utilities/helpers';

/**
 * Cleans and processes question title text
 * @param {string} title
 * @returns {string}
 */
export const cleanQuestionTitle = (title) => {
  if (!title) return '';
  return title?.replace(/\\"/g, '"').replace(/\s+/g, ' ').trim();
};

const getSortableValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
  }

  if (Array.isArray(value)) {
    for (let i = value.length - 1; i >= 0; i -= 1) {
      const candidate = getSortableValue(value[i]);
      if (candidate !== Number.NEGATIVE_INFINITY) return candidate;
    }
  }

  if (value && typeof value === 'object') {
    if (typeof value.y === 'number') {
      return Number.isFinite(value.y) ? value.y : Number.NEGATIVE_INFINITY;
    }
    if (typeof value.value === 'number') {
      return Number.isFinite(value.value) ? value.value : Number.NEGATIVE_INFINITY;
    }
  }

  const parsed = Number(value);
  if (!Number.isNaN(parsed) && Number.isFinite(parsed)) return parsed;

  return Number.NEGATIVE_INFINITY;
};

export const buildCompareByGroups = ({ globalFilters, selectedCompareBy }) => {
  if (Array.isArray(selectedCompareBy?.groups)) return selectedCompareBy.groups;

  const selectedCompareByValue =
    typeof selectedCompareBy === 'string' ? selectedCompareBy : selectedCompareBy?.value;

  switch (selectedCompareByValue) {
    case 'overall':
      return [{ id: 'Overall', name: 'Overall' }];
    case 'quota_group':
      return (
        globalFilters?.quotaGroups?.map(({ instantSurveyGroupRef, name }) => ({
          id: instantSurveyGroupRef,
          name,
        })) || []
      );
    default:
      return (
        globalFilters?.demographicFilters
          ?.find(({ id }) => id === selectedCompareByValue)
          ?.compareBy?.map(({ id, label }) => ({ id, name: label })) || []
      );
  }
};

const chartHasNumericXPoints = (chart) =>
  Array.isArray(chart?.series) &&
  chart.series.some(
    (series) =>
      Array.isArray(series?.data) &&
      series.data.some((point) => {
        if (Array.isArray(point)) {
          const x = point[0];
          return typeof x === 'number' && Number.isFinite(x);
        }

        if (point && typeof point === 'object') {
          const x = point.x;
          return typeof x === 'number' && Number.isFinite(x);
        }

        return false;
      })
  );

const sortChartDataForExport = ({
  chart,
  compareByGroups,
  compareByValue,
  sortByValue,
  sortOrder,
}) => {
  if (!chart || !Array.isArray(chart?.series) || !chart.series.length) return chart;

  const xAxis = chart?.xAxis;
  const hasCategories = Array.isArray(xAxis?.categories) && xAxis.categories.length > 0;
  const axisType = xAxis && typeof xAxis.type === 'string' ? xAxis.type : undefined;
  const isTrulyNumericAxis =
    !hasCategories &&
    (axisType === 'linear' ||
      axisType === 'datetime' ||
      axisType === 'logarithmic' ||
      axisType === 'numeric');

  if (chartHasNumericXPoints(chart) && isTrulyNumericAxis) return chart;

  const normalizedSortOrder =
    typeof sortOrder === 'string' ? sortOrder : sortOrder?.value || sortOrder?.id;
  const normalizedSortByValue =
    typeof sortByValue === 'string' ? sortByValue : sortByValue?.value || sortByValue?.id;

  const categories =
    chart?.xAxis?.categories ||
    (Array.isArray(chart?.series?.[0]?.data)
      ? chart.series[0].data.map((point, index) =>
          point && typeof point === 'object' && point.name != null
            ? String(point.name)
            : String(index)
        )
      : undefined);

  if (!Array.isArray(categories) || categories.length < 2) return chart;
  if (!normalizedSortOrder || normalizedSortOrder === 'default') return chart;

  const compareValue = typeof compareByValue === 'string' ? compareByValue : compareByValue?.value;
  const isOverall = compareValue == null || compareValue === 'overall';
  const hasSortSelection = normalizedSortByValue && normalizedSortByValue !== 'default';

  if (!isOverall && !hasSortSelection) return chart;

  let targetSeries;

  if (!hasSortSelection) {
    targetSeries = chart.series[0];
  } else {
    const sortToken = String(normalizedSortByValue);
    const candidates = [sortToken];

    if (Array.isArray(compareByGroups)) {
      const matchingGroup = compareByGroups.find(
        (group) => group?.id != null && String(group.id) === sortToken
      );
      if (matchingGroup?.name != null) candidates.push(String(matchingGroup.name));
    }

    targetSeries =
      candidates
        .map((token) =>
          chart.series.find(
            (series) =>
              (series?.id != null && String(series.id) === token) ||
              (series?.name != null && String(series.name) === token)
          )
        )
        .find(Boolean) || chart.series[0];
  }

  if (!targetSeries || !Array.isArray(targetSeries.data)) return chart;

  const categoryValuePairs = categories.map((category, index) => ({
    category,
    sortValue: getSortableValue(targetSeries.data[index]),
  }));

  const sortedCategoryOrder = categoryValuePairs
    .slice()
    .sort((a, b) => {
      const difference = a.sortValue - b.sortValue;
      return normalizedSortOrder === 'desc' ? -difference : difference;
    })
    .map(({ category }) => category);

  const categoryIndexMap = new Map(categories.map((category, index) => [category, index]));

  const remappedSeries = chart.series.map((series) => {
    const values = Array.isArray(series.data) ? series.data : [];
    const remappedValues = sortedCategoryOrder.map((category) => {
      const originalIndex = categoryIndexMap.get(category);
      return originalIndex == null ? null : values[originalIndex];
    });

    return {
      ...series,
      data: remappedValues,
    };
  });

  return {
    ...chart,
    series: remappedSeries,
    xAxis: {
      ...(chart.xAxis || {}),
      categories: sortedCategoryOrder,
    },
  };
};

/**
 * Derives the human-readable "applied filters" list for the metadata slide from Survey's URL params
 * and global filters (quota groups, demographic filters, question options). This is Survey-domain
 * logic; the generic createMetadataSlide renderer just takes the resulting strings.
 * @param {Object} options
 * @param {Object} options.globalFilters
 * @param {Array}  [options.questionsRef]
 * @param {URLSearchParams} options.searchParams
 * @param {Object} [options.survey]
 * @param {Array}  [options.surveyQuestions]
 * @returns {string[]}
 */
const getAppliedFilters = ({
  globalFilters,
  questionsRef = [],
  searchParams,
  survey = null,
  surveyQuestions = [],
}) => {
  const filters = [];

  if (!searchParams || !globalFilters) return filters;

  const quotaGroupFilter = searchParams.get(SEARCH_PARAM_KEYS.QUOTA_GROUP);
  let selectedGroupIds = [];
  if (quotaGroupFilter) {
    selectedGroupIds = quotaGroupFilter.split(',').filter(Boolean);
  } else if (globalFilters?.quotaGroups) {
    selectedGroupIds = globalFilters.quotaGroups.map(
      ({ instantSurveyGroupRef }) => instantSurveyGroupRef
    );
  }

  if (selectedGroupIds.length > 0 && survey?.groups && Array.isArray(survey.groups)) {
    selectedGroupIds.forEach((groupId) => {
      const group = survey.groups.find((g) => g && String(g.id) === String(groupId));
      if (group && group.name) {
        filters.push(group.name);
      }
    });
  }

  if (globalFilters?.demographicFilters) {
    globalFilters.demographicFilters.forEach(({ id, label }) => {
      const filterValue = searchParams.get(id);
      if (filterValue && filterValue.split(',').filter(Boolean).length > 0) {
        filters.push(label);
      }
    });
  }

  const questionOptionFilter = searchParams.get(SEARCH_PARAM_KEYS.QUESTION_OPTION);
  if (questionOptionFilter && questionOptionFilter.split(',').filter(Boolean).length > 0) {
    const selectedOptionIds = questionOptionFilter.split(',').filter(Boolean);
    const filteredQuestions = new Set();

    const questionsToCheck =
      Array.isArray(surveyQuestions) && surveyQuestions.length > 0
        ? surveyQuestions
        : Array.isArray(questionsRef) && questionsRef.length > 0
          ? questionsRef
          : [];

    if (questionsToCheck.length > 0) {
      selectedOptionIds.forEach((optionId) => {
        questionsToCheck.forEach((question, index) => {
          if (question && question.options && Array.isArray(question.options)) {
            const hasOption = question.options.some(
              (option) => option && String(option.id) === String(optionId)
            );
            if (hasOption) {
              filteredQuestions.add(`Q ${index + 1}`);
            }
          }
        });
      });
    }

    filters.push(
      filteredQuestions.size > 0
        ? Array.from(filteredQuestions).sort().join(', ')
        : 'Question Options'
    );
  }

  return filters;
};

/**
 * Exports survey responses and key findings to a PowerPoint presentation
 * @param {Object} options - Export options
 * @param {Object} options.details - Survey details
 * @param {Object} options.displayAs - Display format object with label and value properties
 * @param {Object} options.globalFilters - Global filter options
 * @param {Object} options.insightsDocument - Insights document data
 * @param {Object} options.keyFindingsMap - Map of question IDs to key findings
 * @param {string} options.publicToken - Public token for the survey
 * @param {Object} options.questionsRef - Reference to survey questions
 * @param {Object} options.responseData - Survey response data
 * @param {URLSearchParams} options.searchParams - URL search parameters
 * @param {Object} [options.selectedAnswerOptionsByQuestion] - Answer Options selection per question id
 * @param {Object} options.selectedCompareBy - Selected comparison option
 * @param {Object} options.survey - Survey object
 * @param {Array} options.surveyQuestions - Array of survey questions
 * @param {string} options.surveyId - Survey ID
 */
export const exportPowerPoint = async ({
  areFiltersApplied: areFiltersAppliedOverride,
  details,
  displayAs,
  getWordCloudImages,
  globalFilters,
  insightsDocument,
  keyFindingsMap = {},
  questionsRef,
  responseCounts,
  responseData,
  searchParams,
  selectedAnswerOptionsByQuestion = {},
  selectedCompareBy,
  sortByGroupByQuestion = {},
  sortOrderByQuestion = {},
  survey,
  surveyQuestions,
  theme,
  wordCloudImagesMap = {},
}) => {
  const surveyTitle = survey?.title || insightsDocument.title;
  const surveyResponsesUrl = window.location.href;
  const surveyResponsesArray = responseData ? Object.values(responseData).filter(Boolean) : [];
  const reportSurveyQuestions = surveyQuestions.filter(
    (question) => !isLanguageScreenerQuestion(question)
  );
  const reportQuestionsRef = questionsRef?.filter(
    (question) => !isLanguageScreenerQuestion(question)
  );
  const surveyReportType = 'Survey Results';

  const {
    [SEARCH_PARAM_KEYS.QUESTION_OPTION]: selectedOptions,
    [SEARCH_PARAM_KEYS.QUOTA_GROUP]: selectedGroups,
    ...otherParams
  } = Object.fromEntries(searchParams);

  const areLegacyFiltersApplied =
    globalFilters?.demographicFilters?.some(({ id }) => otherParams[id]?.split(',')?.length > 0) ||
    selectedOptions?.split(',')?.length > 0 ||
    selectedGroups?.split(',')?.length > 0;
  const areFiltersApplied = areFiltersAppliedOverride ?? areLegacyFiltersApplied;

  const pptx = new PptxGenJS();

  // Add title slide
  createTitleSlide({
    pptx,
    reportName: surveyTitle,
    reportType: 'Verified Voices',
    useLargeSpacing: true,
  });

  // Add metadata slide — Survey derives the primitives; the renderer stays generic.
  createMetadataSlide({
    appliedFilters: getAppliedFilters({
      globalFilters,
      questionsRef: reportQuestionsRef,
      searchParams,
      survey,
      surveyQuestions: reportSurveyQuestions,
    }),
    compareBy: selectedCompareBy?.label || '',
    pptx,
    questionsCount: reportSurveyQuestions.length,
    quotaGroupsCount: survey?.groups?.length > 1 ? survey.groups.length : 1,
    reportType: surveyReportType,
    reportUrl: surveyResponsesUrl,
    sourceLabel: surveyTitle,
    sourceUrl: surveyResponsesUrl,
    totalResponses:
      responseCounts?.filteredResponses ?? details?.completes ?? insightsDocument?.completed ?? 0,
  });

  // Get displayAs value and compareBy object once for all question processing
  const displayAsValue = displayAs.value;
  const compareByGroupsForExport = buildCompareByGroups({
    globalFilters,
    selectedCompareBy,
  });

  // Process ALL questions in their original order to maintain question sequence
  for (const [index, question] of reportSurveyQuestions.entries()) {
    const questionData = surveyResponsesArray.find(({ id }) => id === question.id);
    if (!questionData) continue;

    try {
      switch (question.type) {
        case QUESTION_TYPES.CONT_SUM.value:
        case QUESTION_TYPES.CONTSUM.value: // INSRV-6985: Calypso passes 'contsum' as question_type
          createContSumChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.richText || question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;
        case QUESTION_TYPES.ESSAY.value: {
          // Only create text slides when comparing by overall or quota group
          if (!['overall', 'quota_group'].includes(selectedCompareBy?.value)) {
            continue;
          }

          // Only include text summaries if no filters are applied, using exact UI logic
          const questionTextSummaries = getTextSummariesForPowerPoint({
            areFiltersApplied,
            question,
            selectedCompareBy,
            surveyResponseSummariesMap: { questionSummaries: keyFindingsMap.questionSummaries },
          });

          createTextSlide({
            compareBy: selectedCompareBy,
            pptx,
            question,
            questionNumber: index + 1,
            reportType: 'OPEN_END_QUESTION',
            reportUrl: surveyResponsesUrl,
            textSummaries: questionTextSummaries || [],
            title: surveyTitle,
          });

          // Add Word Cloud slide if pre-rendered images are provided (via map or callback)
          try {
            let wordCloudImages =
              wordCloudImagesMap?.[question.id] ||
              (getWordCloudImages
                ? await getWordCloudImages(question, selectedCompareBy)
                : undefined);

            const resolveWordCloudPng = async ({ question, segment }) => {
              try {
                const qData = responseData?.[question.id];
                const seg = segment || qData?.segments?.[0];
                const wordCloudData = seg?.chart?.series?.[0]?.data || [];
                const filteredData = wordCloudData
                  .filter((p) => p?.weight >= 7)
                  .map((p) => ({ name: p?.name, weight: p?.weight }));
                const isDark = theme === THEMES_DARK.value;
                const palette = isDark ? WORD_CLOUD_COLORS.dark : WORD_CLOUD_COLORS.light;
                const coloredData = filteredData.map((point, index) => ({
                  ...point,
                  color: palette[index % palette.length],
                }));

                if (!filteredData?.length) return undefined;

                const container = document.createElement('div');
                container.style.position = 'absolute';
                container.style.left = '-10000px';
                container.style.width = '900px';
                document.body.appendChild(container);

                const chartOptions = buildWordCloudChartOptions({
                  data: coloredData,
                  titleText: undefined,
                  tooltipEnabled: false,
                });

                const chart = Highcharts.chart(container, chartOptions);
                const svg = chart.getSVG();
                const dataUrl = await svgToPngDataUrl(svg, 900, 600);
                chart.destroy();
                document.body.removeChild(container);
                return dataUrl || undefined;
              } catch (e) {
                return undefined;
              }
            };

            if (!wordCloudImages) {
              const isOverall =
                selectedCompareBy?.value === 'overall' || selectedCompareBy === 'overall';

              if (isOverall) {
                const overall = await resolveWordCloudPng({
                  compareBy: selectedCompareBy,
                  format: 'png',
                  question,
                  segment: undefined,
                });
                if (overall) wordCloudImages = { overall, segments: {} };
              } else {
                const segs = responseData?.[question.id]?.segments || [];
                const segments = {};
                for (const seg of segs) {
                  const name = seg?.name ?? seg?.id ?? String(seg);
                  const dataUrl = await resolveWordCloudPng({
                    compareBy: selectedCompareBy,
                    format: 'png',
                    question,
                    segment: seg,
                  });
                  if (dataUrl) segments[name] = dataUrl;
                }
                if (Object.keys(segments).length)
                  wordCloudImages = { overall: undefined, segments };
              }
            }

            if (wordCloudImages) {
              await createWordCloudSlide({
                compareBy: selectedCompareBy,
                pptx,
                questionNumber: index + 1,
                questionTitle: question.richText || question.title,
                reportType: 'OPEN_END_QUESTION',
                reportUrl: surveyResponsesUrl,
                title: surveyTitle,
                wordCloudImages,
              });
            }
          } catch (err) {
            // Fail quietly on Word Cloud slide creation
          }
          break;
        }
        case QUESTION_TYPES.MAX_DIFF.externalType:
        case QUESTION_TYPES.MAX_DIFF.value: // INSRV-7318: Calypso passes 'MAX-DIFF' as question_type
          createMaxDiffChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.richText || question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });

          createMaxDiffTableSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.richText || question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;

        case QUESTION_TYPES.NUMERIC.externalType:
        case QUESTION_TYPES.NUMERIC.value: // ensuring this works for Calypso as well as Alchemer
          createNumericTableSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.richText || question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;
        case QUESTION_TYPES.RANK.value:
        case QUESTION_TYPES['RANK-DRAGDROP'].value: // INSRV-7027: Calypso passes 'RANK-DRAGDROP' as question_type
          createRankChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.richText || question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          createRankTableSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.richText || question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;

        case QUESTION_TYPES.TABLE.value:
        case QUESTION_TYPES['TABLE-RADIO'].value: {
          // INSRV-7223: Calypso passes 'TABLE-RADIO' as question_type
          const gridDataSlices = questionData.dataByCompareBy
            ? Object.entries(questionData.dataByCompareBy).map(([groupName, groupData]) => {
                const compareByLabel = [
                  selectedCompareBy?.label || selectedCompareBy?.value,
                  groupName,
                ]
                  .filter(Boolean)
                  .join(' - ');

                return {
                  compareBy: {
                    ...selectedCompareBy,
                    label: compareByLabel,
                  },
                  questionData: groupData,
                };
              })
            : [{ compareBy: selectedCompareBy, questionData }];

          gridDataSlices.forEach(({ compareBy, questionData: gridQuestionData }) => {
            createGridChartSlide({
              compareBy,
              displayAs: displayAsValue,
              pptx,
              questionData: gridQuestionData,
              questionNumber: index + 1,
              questionTitle: question.richText || question.title,
              reportDateRange: undefined,
              reportType: surveyReportType,
              reportUrl: surveyResponsesUrl,
              title: surveyTitle,
            });
            createGridTableSlide({
              compareBy,
              displayAs: displayAsValue,
              pptx,
              questionData: gridQuestionData,
              questionNumber: index + 1,
              questionTitle: question.richText || question.title,
              reportType: surveyReportType,
              reportUrl: surveyResponsesUrl,
              title: surveyTitle,
            });
          });
          break;
        }

        case QUESTION_TYPES.VAN_WESTENDORP.value: {
          const segments = questionData.segments || [];

          segments.forEach((segment) => {
            createVanWestendorpChartSlide({
              chart: displayAsValue === 'count' ? segment.chartCount : segment.chartPct,
              pptx,
              questionNumber: index + 1,
              questionTitle: question.richText || question.title,
              reportType: surveyReportType,
              reportUrl: surveyResponsesUrl,
              slideSuffix: segments.length > 1 ? ` Compared by: ${segment.name || segment.id}` : '',
              table: segment.table,
              title: surveyTitle,
            });
          });
          break;
        }

        default: {
          if (isSequentialMonadicMultiSelectQuestion(question)) {
            const table =
              displayAsValue === 'count' ? questionData.tableCount : questionData.tablePct;

            createTableSlide({
              pptx,
              reportType: surveyReportType,
              reportUrl: surveyResponsesUrl,
              slideTitle: `Q${index + 1}. ${cleanQuestionTitle(question.title)}`,
              table,
            });
            break;
          }

          // Get chart data
          const useCountData = displayAs?.value === 'count';
          let chartData;
          if (questionData.chartCount || questionData.chartPct) {
            chartData = useCountData ? questionData.chartCount : questionData.chartPct;
          } else if (questionData.results?.length) {
            chartData = {
              series: questionData.results,
              title: question.title,
            };
          }

          // Sequential monadic charts have no top-level `series` until an answer option is
          // picked, so normalize against the selection first, the way the Results tab does. An
          // empty selection leaves no series, which skips the question as the tab hides the chart.
          let answerOptionLabels = [];
          if (chartData && !Array.isArray(chartData.series)) {
            answerOptionLabels = (
              selectedAnswerOptionsByQuestion[question.id] ??
              getDefaultAnswerOptionSelection(question)
            ).map(({ value }) => value);

            chartData = normalizeAnswerOptionChart({
              activeChart: chartData,
              selectedAnswerOptions: answerOptionLabels,
            });
          }

          if (chartData?.series?.length) {
            // Get key findings for this question using the same logic as UI
            const keyFindings = getNumericKeyFindingsForPowerPoint({
              areFiltersApplied,
              question,
              selectedCompareBy,
              surveyResponseSummariesMap: { questionSummaries: keyFindingsMap.questionSummaries },
            });

            const sortedChartData = sortChartDataForExport({
              chart: chartData,
              compareByGroups: compareByGroupsForExport,
              compareByValue: selectedCompareBy?.value ?? selectedCompareBy,
              sortByValue: sortByGroupByQuestion?.[question.id] ?? 'default',
              sortOrder: sortOrderByQuestion?.[question.id] ?? 'default',
            });

            // Only one answer option's breakdown is charted, so the slide names it.
            const title = question.richText || question.title;
            const questionTitle = answerOptionLabels.length
              ? `${title} — ${answerOptionLabels.map((label) => `"${label}"`).join(', ')}`
              : title;

            createSurveyChartSlide({
              chart: sortedChartData,
              compareBy: selectedCompareBy,
              keyFindings,
              pptx,
              questionNumber: index + 1,
              questionTitle,
              reportType: surveyReportType,
              reportUrl: surveyResponsesUrl,
              title: surveyTitle,
            });
          }
          break;
        }
      }
    } catch (error) {
      // Skip questions that fail to process
    }
  }

  // Generate the file
  await pptx.writeFile({ fileName: `${surveyTitle}.pptx` });
};
